/**
 * Dante audio routing as stored in a studio config.
 *
 * The config mirrors the whole Dante network of a studio: every device with
 * all its channels, their names and the rx subscriptions. Routes are stored
 * by channel *index*; Dante itself subscribes by tx channel *name*, so the
 * bridge translates an index to the tx channel's current name whenever it
 * talks to a device.
 */
// ─── Helpers (shared by bridge and offline editing) ─────────
export function emptyDanteConfig() {
    return { devices: [] };
}
export function danteChannelKey(device, index) {
    return `${device}/${index}`;
}
export function parseDanteChannelKey(key) {
    const slash = key.lastIndexOf("/");
    if (slash <= 0)
        return null;
    const index = Number(key.slice(slash + 1));
    if (!Number.isInteger(index) || index < 1)
        return null;
    return { device: key.slice(0, slash), index };
}
/** The name a channel currently goes by: its custom name or its default */
export function danteChannelName(channel) {
    return channel.name ?? channel.default_name;
}
export function findDanteDevice(config, name) {
    return config.devices.find((d) => d.name === name);
}
/**
 * The (channel name, device name) pair Dante needs to subscribe to a source,
 * or null when the source's tx channel is not known.
 */
export function resolveDanteSourceName(config, source) {
    if ("unresolved_name" in source) {
        return { channel: source.unresolved_name, device: source.device };
    }
    const tx = findDanteDevice(config, source.device)?.tx.find((c) => c.index === source.index);
    return tx ? { channel: danteChannelName(tx), device: source.device } : null;
}
/** Map a live subscription (by name) back to an index-based source */
export function danteSourceFromName(config, device, channel) {
    const tx = findDanteDevice(config, device)?.tx.find((c) => danteChannelName(c) === channel);
    return tx
        ? { device, index: tx.index }
        : { device, index: null, unresolved_name: channel };
}
export function sameDanteSource(a, b) {
    if (a === null || b === null)
        return a === b;
    if (a.device !== b.device || a.index !== b.index)
        return false;
    if ("unresolved_name" in a && "unresolved_name" in b) {
        return a.unresolved_name === b.unresolved_name;
    }
    return true;
}
function mapDevice(config, name, fn) {
    return {
        ...config,
        devices: config.devices.map((d) => (d.name === name ? fn(d) : d)),
    };
}
export function setDanteRoute(config, rxDevice, rxIndex, source) {
    return mapDevice(config, rxDevice, (d) => ({
        ...d,
        rx: d.rx.map((c) => (c.index === rxIndex ? { ...c, source } : c)),
    }));
}
/**
 * Rename a channel. An empty or default name resets it to the default.
 * Routes are by index, so renaming a tx channel never changes a route.
 */
export function renameDanteChannel(config, device, direction, index, name) {
    const rename = (c) => {
        if (c.index !== index)
            return c;
        const trimmed = name?.trim() ?? "";
        return {
            ...c,
            name: trimmed === "" || trimmed === c.default_name ? null : trimmed,
        };
    };
    return mapDevice(config, device, (d) => direction === "tx"
        ? { ...d, tx: d.tx.map(rename) }
        : { ...d, rx: d.rx.map(rename) });
}
/**
 * Remove a device. Receivers elsewhere that listened to it keep their
 * subscription as an unresolved name, so nothing is silently dropped.
 */
export function removeDanteDevice(config, name) {
    const removed = findDanteDevice(config, name);
    if (!removed)
        return config;
    return {
        ...config,
        devices: config.devices
            .filter((d) => d.name !== name)
            .map((d) => ({
            ...d,
            rx: d.rx.map((c) => {
                if (c.source?.device !== name || c.source.index === null)
                    return c;
                const resolved = resolveDanteSourceName(config, c.source);
                return {
                    ...c,
                    source: resolved
                        ? { device: name, index: null, unresolved_name: resolved.channel }
                        : null,
                };
            }),
        })),
    };
}
